import { Head, Link, router } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Avatar from '@/components/Avatar'
import { BarChart, Heatmap } from '@/components/Charts'
import { ToastStack, CelebrationCard } from '@/components/Celebrate'
import { t } from '@/lib/i18n'

interface TaskRow {
  id: number; title: string; points: number; stars: string
  cycle_label: string; available: boolean; goal_title: string
}
interface Props {
  name: string
  avatar_url: string | null
  // 公开页只接收渲染所需的精简字段
  stats: { total: number; streak: number }
  daily: Record<string, number>
  badges_earned: number
  badges_total: number
  can_check_in: boolean
  viewer_signed_in: boolean
  uuid: string
  tasks?: TaskRow[]
}

function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="bg-card border border-line rounded-2xl px-4 py-3.5 text-center">
      <div className="text-[26px] font-extrabold leading-tight">{value}</div>
      <div className="text-muted text-[13px] mt-1">{label}</div>
    </div>
  )
}

// 分享页是本产品唯一的自然获客通道（看板上的「🔗 分享打卡页」把链接发给别人）。
// 在这之前这个页面没有任何注册入口，访客看完战绩就走，转化率结构性为 0。
function VisitorCta({ signedIn }: { signedIn: boolean }) {
  if (signedIn) {
    // 已登录的人多半就是本人或共管的家长，弹「免费开始」是噪音，给个回看板的路。
    return (
      <div className="text-center">
        <Link href="/dashboard" className="text-brand text-sm font-semibold">
          {t('public_profile.back_to_dashboard')}
        </Link>
      </div>
    )
  }

  return (
    <section className="bg-gradient-to-br from-tint to-tint2 border border-line rounded-2xl p-5 text-center">
      <div className="font-extrabold text-[17px]">{t('public_profile.cta_title')}</div>
      <p className="text-muted text-sm mt-2 leading-relaxed max-w-[420px] mx-auto">
        {t('public_profile.cta_body')}
      </p>
      <div className="flex gap-2.5 justify-center flex-wrap mt-4">
        <Link
          href="/registration/new"
          className="bg-brand text-white font-bold text-[15px] px-5 py-2.5 rounded-xl hover:opacity-90"
        >
          {t('public_profile.cta_start')}
        </Link>
        {/* 直接落到落地页的「怎么用」那一节，别让访客自己找。这里用原生 a 而不是
            Inertia Link：Link 的客户端跳转不会处理 hash 锚点滚动。 */}
        <a
          href="/#how"
          className="bg-card border border-line font-bold text-[15px] px-5 py-2.5 rounded-xl hover:border-brand"
        >
          {t('public_profile.cta_how')}
        </a>
      </div>
      <div className="text-faint text-xs mt-3">{t('public_profile.cta_note')}</div>
    </section>
  )
}

export default function Show({ name, avatar_url, stats, daily, badges_earned, badges_total, can_check_in, viewer_signed_in, uuid, tasks }: Props) {
  const checkIn = (taskId: number) =>
    router.post(`/u/${uuid}/checkin/${taskId}`, {}, { preserveScroll: true })

  return (
    <div className="min-h-screen px-4 py-8">
      <Head title={t('public_profile.head_title', { name })} />
      <ToastStack />
      <CelebrationCard />
      {/* 这里**刻意没有** <SpinWheel/>，别当成漏掉的收尾工作补上。
          分享页按 URL 里的 :uuid 认人，而 props.spin 与 POST /spin 认的是会话里的
          current_person。孩子 A 的分享页在一个已经切到孩子 B 的会话里打开时，自动弹
          出来的盘会画着 B 的盘面、点一下烧掉的是 B 今天那次机会，分还落在 B 的账上。
          Celebration 之所以能在这儿用，是因为它只是把服务端算好的文案摆出来。 */}
      <div className="max-w-[760px] mx-auto grid gap-5">
        <header className="text-center">
          {/* 原来是个死 div：好奇的访客没有任何路径回到产品首页 */}
          <Link
            href="/"
            className="inline-block font-extrabold text-2xl bg-gradient-to-r from-brand to-brand2 bg-clip-text text-transparent"
          >
            📦 HabitChest
          </Link>
          {/* 头像比标题大一号地摆在标题上方：这一页给的是「谁做到了什么」，
              而爷爷奶奶点开链接时先认的是脸。没传头像时仍是首字圆圈。 */}
          <Avatar name={name} url={avatar_url} size={64} className="mt-4 mx-auto" />
          <h1 className="text-[20px] font-bold mt-3 mb-0">{t('public_profile.heading', { name })}</h1>
        </header>

        <div className="grid grid-cols-3 gap-3">
          <Stat value={stats.total} label={t('public_profile.total')} />
          <Stat value={t('dashboard.days', { count: stats.streak })} label={t('public_profile.streak')} />
          <Stat value={`${badges_earned}/${badges_total}`} label={t('public_profile.badges')} />
        </div>

        <section className="bg-card border border-line rounded-2xl p-4">
          <div className="font-bold mb-2">📈 {t('dashboard.trend')}</div>
          <BarChart daily={daily} />
        </section>

        <section className="bg-card border border-line rounded-2xl p-4">
          <div className="font-bold mb-2">🟩 {t('public_profile.heatmap')}</div>
          <div style={{ overflowX: 'auto' }}><Heatmap daily={daily} /></div>
        </section>

        {can_check_in && tasks && (
          <section className="bg-card border border-line rounded-2xl p-4">
            <div className="font-bold mb-3">✅ {t('public_profile.today')}</div>
            {tasks.length === 0 ? (
              <div className="text-muted text-sm">{t('public_profile.all_done')}</div>
            ) : (
              <ul className="grid gap-2">
                {/* 形参叫 task：t 是译文查表函数，同名会遮住它 */}
                {tasks.map((task) => (
                  <li key={task.id} className="flex items-center justify-between border border-line rounded-xl px-3 py-2.5">
                    <div>
                      <div className="font-medium">{task.title}</div>
                      <div className="text-muted text-xs mt-0.5">{task.goal_title} · {task.cycle_label} · {task.stars}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => checkIn(task.id)}
                      className="shrink-0 bg-brand text-white text-sm font-medium rounded-lg px-3 py-1.5"
                    >
                      +{task.points} {t('checkin.check_in')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}

        <VisitorCta signedIn={viewer_signed_in} />

        <footer className="text-center text-muted text-xs py-4">
          {t('public_profile.footer')}
        </footer>
      </div>
    </div>
  )
}
